import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Search } from 'lucide-react';

const BlogPage = () => {
  const [blogData, setBlogData] = useState({ posts: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Fetch latest blog data from public folder on every render
    const fetchBlogData = async () => {
      try {
        const response = await fetch(`/data/blogData.js?t=${Date.now()}`);
        const text = await response.text();
        // Parse the JavaScript file to get the data
        const script = document.createElement('script');
        script.textContent = text;
        document.body.appendChild(script);
        
        if (window.blogData) {
          setBlogData(window.blogData);
        }
        document.body.removeChild(script);
      } catch (error) {
        console.error('Error loading blog data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogData();
  }, []);

  const filteredPosts = blogData.posts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-dark-600 font-body">Loading blog...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-50">
      
      {/* Hero */}
      <div className="bg-gradient-primary py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl text-dark-900 md:text-5xl font-display font-bold mb-4">
            Furniture Blog
          </h1>
          <p className="text-xl text-dark-700 max-w-2xl mx-auto font-body">
            Tips, guides, and inspiration for your home
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* Search & Filter */}
        <div className="mb-12">
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-dark-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {blogData.categories.map(cat => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-lg font-body font-medium transition-smooth ${
                  selectedCategory === cat
                    ? 'bg-gradient-primary'
                    : 'bg-white text-dark-700 hover:bg-dark-100'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map(post => (
            <article key={post.id} className="bg-white rounded-xl shadow-card hover:shadow-soft transition-smooth overflow-hidden">
              <div className="h-48 bg-dark-200 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  onError={(e) => e.target.src = 'https://via.placeholder.com/400x300?text=Blog+Image'}
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold">
                    {post.category}
                  </span>
                  <span className="text-sm text-dark-500">{post.readTime}</span>
                </div>
                <h2 className="text-xl font-display font-bold text-dark-900 mb-3 line-clamp-2">
                  {post.title}
                </h2>
                <p className="text-dark-600 font-body mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-dark-100">
                  <div className="flex items-center gap-2 text-sm text-dark-500">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  <Link
                    to={`/blog/${post.slug}`}
                    className="flex items-center gap-1 text-primary-600 hover:text-primary-700 font-semibold text-sm"
                  >
                    Read More <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-dark-500 font-body">No articles found. Try different filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
